import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tabs, PageHeader } from '@airclou/ui'
import { CredentialsTab } from './CredentialsTab'
import { ActiveSessionsTab } from './ActiveSessionsTab'
import { ActivityLogTab } from './ActivityLogTab'
import { RecoveryTab } from './RecoveryTab'
import { LoginSecurityTab } from './LoginSecurityTab.tsx'
import { RateLimitsTab } from './RateLimitsTab'

export function SecurityPage() {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const params = useParams()
  const initialTab = (params.tab as string) || 'credentials'
  const [activeTab, setActiveTab] = useState(initialTab)

  const tabs = [
    {
      id: 'credentials',
      label: t('security.credentials'),
      content: <CredentialsTab />
    },
    {
      id: 'sessions',
      label: t('security.activeSessions'),
      content: <ActiveSessionsTab />
    },
    {
      id: 'activity',
      label: t('security.activityLog'),
      content: <ActivityLogTab />
    },
    {
      id: 'login-security',
      label: t('security.loginSecurity'),
      content: <LoginSecurityTab />
    },
    {
      id: 'recovery',
      label: t('security.recovery'),
      content: <RecoveryTab />
    },
    {
      id: 'rate-limits',
      label: t('security.rateLimits'),
      content: <RateLimitsTab />
    }
  ]

  // Keep URL and state in sync
  useEffect(() => {
    const tabIds = tabs.map(t => t.id)
    const urlTab = (params.tab as string) || 'credentials'

    if (!tabIds.includes(urlTab)) {
      // Unknown tab -> redirect to default
      navigate('/security/credentials', { replace: true })
      return
    }

    if (urlTab !== activeTab) {
      setActiveTab(urlTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.tab])

  const handleTabChange = (id: string) => {
    if (id !== activeTab) {
      setActiveTab(id)
      navigate(`/security/${id}`)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('security.title')} description={t('security.subtitle')} />

      {/* Every tab here is a single-column form or list and takes the standard
          max-w-2xl cap. Login security is the exception: its trusted-devices
          DataTable declares 1020px of column widths, which DataTable applies as
          a per-column minWidth inside overflow-x-auto — at 2xl (672px) the
          table scrolls sideways with the rest of the viewport left empty. That
          tab caps its own settings card instead, since only the table needs
          the room. */}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        contentClassName={activeTab === 'login-security' ? undefined : 'max-w-2xl'}
      />
    </div>
  )
}
